<template>
    <page-loading v-if="loading"></page-loading>
    <form-container v-else>
        <page-header class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a
                    :href="`/settings/teams/${teamId}#/membership`"
                    class="no-underline! text-sky-600! text-base font-normal"
                >
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Users
                </a>
            </template>

            <div>
                <div>
                    {{ user.name }}
                </div>

                <div class="font-normal text-base">{{ user.email }}</div>
            </div>

            <template v-slot:actions>
                <div class="min-w-[240px] font-normal text-base">
                    <vx-label>Team Role</vx-label>
                    <vx-selectable
                        v-if="canEditRole"
                        :modelValue="user.role"
                        @update:modelValue="onRoleChange"
                        :reduce="(option: RoleOption) => option.value"
                        :options="roleOptions"
                        :clearable="false"
                        :disabled="updating"
                        data-test="role-select"
                        class="w-[240px]"
                    />
                    <div v-else class="flex items-center text-base font-normal">
                        {{ TEAM_USER_ROLES[user.role] || user.role }}
                    </div>
                </div>
            </template>
        </page-header>

        <div class="grid grid-cols-12">
            <page-sidebar appearance="nested" :links="sidebarLinks"></page-sidebar>

            <slot></slot>
        </div>
    </form-container>
</template>

<script lang="ts" setup>
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, inject, onMounted, ref } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import type { Features } from '~/provides/features.types';
import { TEAM_USER_ROLES, teamRolesOptions, type RoleOption } from '~/constants/roles';
import { useTeamUserDetails } from './useTeamUserDetails';
import { usersClient } from '~/services';
import { VxLabel, VxSelectable } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const FEATURES = inject('FEATURES') as Features;

const props = defineProps<{
    teamId: number;
    userId: number;
}>();

const roleOptions = teamRolesOptions({ exclude: ['owner'] });

const { user, loading, mount } = useTeamUserDetails();

onMounted(() => {
    mount(props.teamId, props.userId);
});

const currentUserId = Spark.state.user.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const canEditRole = computed(() => {
    return isAdminOrOwner && user.value.role !== 'owner' && user.value.id !== currentUserId;
});

const toasted = useToasted();
let activeToastId: number | null = null;

const updating = ref(false);

const onRoleChange = async (newRole: keyof typeof TEAM_USER_ROLES) => {
    if (newRole === user.value.role || updating.value) {
        return;
    }

    const previousRole = user.value.role;
    updating.value = true;

    try {
        await usersClient.updateUser(props.teamId, user.value.id, { role: newRole });
        user.value.role = newRole;

        if (activeToastId !== null) {
            toasted.dismiss(activeToastId);
        }

        const toastConfig = {
            title: `${user.value.name || user.value.email} role updated to ${TEAM_USER_ROLES[newRole]}`,
            type: 'success',
            action: {
                label: 'Undo',
                onClick: async () => {
                    toasted.dismiss(activeToastId);
                    activeToastId = null;
                    try {
                        await usersClient.updateUser(props.teamId, user.value.id, { role: previousRole });
                        user.value.role = previousRole;
                    } catch (e) {
                        toasted.error(catchRequestError(e));
                    }
                },
            },
        };
        activeToastId = toasted.show(toastConfig);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        updating.value = false;
    }
};

const sidebarLinks = computed(() => {
    const links = [];

    if (FEATURES.contact_groups) {
        links.push({
            href: `/settings/teams/${props.teamId}/membership/${props.userId}/target-access`,
            label: 'Target Access',
        });
    }

    return links;
});
</script>
